import { cn } from '@/lib/utils';

import { Product } from '@/decodo/blocks/PricingCards/product';
import Heading from '@/decodo/components/atoms/Heading';
import { TranslationsTypes, useTranslation } from '@/lib/i18n/i18n.client';

export type FeaturesProps = {
  icon: React.ReactNode;
  title?: string;
  features: { name: string }[];
};

export default function Features({ icon, features, title }: FeaturesProps) {
  const { texts } = useTranslation();

  title = title ?? texts.pricing_cards.features.with_each_plan_you_access;

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-6 self-start rounded border border-solid border-gray-200 bg-gray-50 p-6 text-left'
      )}
    >
      <Heading level='h4' classNames={{ root: 'text-gray-950' }}>
        {title}
      </Heading>
      <div
        className={cn('grid grid-cols-1 justify-start gap-3 md:grid-cols-3')}
      >
        {features.map(({ name }, index) => (
          <div className='text-body-m flex gap-2' key={index}>
            <div className='flex max-h-4 min-h-4 min-w-4 max-w-4 items-center justify-center'>
              {icon}
            </div>
            <p className='mf-3 dark:text-gray-dark-400'>{name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const featuresIspGb = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.rotating_on_demand_ips },
  { feature: texts.pricing_cards.features.uptime_99 },
  { feature: texts.pricing_cards.features.unlimited_connections_and_threads },
  { feature: texts.pricing_cards.features.country_level_targeting },
  { feature: texts.pricing_cards.features.unlimited_duration_sessions },
  { feature: texts.pricing_cards.features.available_locations_us_fr_hk_ca },
];

const featuresDedicatedIsp = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.premium_isp_providers },
  { feature: texts.pricing_cards.features.unlimited_duration_sessions },
  { feature: texts.pricing_cards.features.unlimited_traffic_bandwidth },
  { feature: texts.pricing_cards.features.dev_friendly_api },
  { feature: texts.pricing_cards.features.support_24_7 },
  { feature: texts.pricing_cards.features.free_extras_subscription },
];

const featuresIspIp = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.premium_isp_providers },
  { feature: texts.pricing_cards.features.unlimited_duration_sessions },
  { feature: texts.pricing_cards.features.dev_friendly_api },
  { feature: texts.pricing_cards.features.support_24_7 },
  { feature: texts.pricing_cards.features.free_extras_subscription },
];

const featuresDatacenter = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.shared_ips_100k },
  { feature: texts.pricing_cards.features.uptime_99 },
  { feature: texts.pricing_cards.features.us_eu_locations },
  { feature: texts.pricing_cards.features.response_time_0_3s },
  { feature: texts.pricing_cards.features.rotating_static_ips },
  { feature: texts.pricing_cards.features.unlimited_connections_and_threads },
];

const featuresDatacenterIp = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.exclusive_ip_pool },
  { feature: texts.pricing_cards.features.socks5_https },
  { feature: texts.pricing_cards.features.locations_worldwide_8 },
  { feature: texts.pricing_cards.features.rotating_static_ips },
  { feature: texts.pricing_cards.features.response_time_0_3s },
  { feature: texts.pricing_cards.features.whitelisted_ips },
];

const featuresDedicatedDatacenter = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.us_ips_400k },
  { feature: texts.pricing_cards.features.unlimited_traffic_bandwidth },
  { feature: texts.pricing_cards.features.socks5_https },
  { feature: texts.pricing_cards.features.no_hidden_fees },
  { feature: texts.pricing_cards.features.subnets_1500_plus },
  { feature: texts.pricing_cards.features.ip_replacement_option },
];

const featuresSocialMedia = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.results_html_json },
  { feature: texts.pricing_cards.features.success_rate_100 },
  { feature: texts.pricing_cards.features.sync_async_requests },
  { feature: texts.pricing_cards.features.realtime_ondemand_data },
  { feature: texts.pricing_cards.features.easy_integration },
  { feature: texts.pricing_cards.features.browser_fingerprints },
];

const featuresEcommerceCore = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.api_playground },
  { feature: texts.pricing_cards.features.prebuilt_scraper },
  { feature: texts.pricing_cards.features.proxy_management },
  { feature: texts.pricing_cards.features.scalable_plans },
  { feature: texts.pricing_cards.features.geo_targeting },
];

const featuresEcommerceAdvanced = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.api_playground },
  { feature: texts.pricing_cards.features.advanced_rotation },
  { feature: texts.pricing_cards.features.success_rate_100 },
  { feature: texts.pricing_cards.features.geo_targeting },
  { feature: texts.pricing_cards.features.unlimited_connections_and_threads },
  { feature: texts.pricing_cards.features.javascript_rendering },
  { feature: texts.pricing_cards.features.parsed_results },
  { feature: texts.pricing_cards.features.ready_made_templates },
];

const featuresSERP = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.results_html_json },
  { feature: texts.pricing_cards.features.ready_made_scrapers },
  { feature: texts.pricing_cards.features.scheduling },
  { feature: texts.pricing_cards.features.parsed_results },
  { feature: texts.pricing_cards.features.success_rate_100 },
  { feature: texts.pricing_cards.features.sync_async_requests },
  { feature: texts.pricing_cards.features.easy_integration },
  { feature: texts.pricing_cards.features.api_playground },
];

const featuresWebCore = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.api_playground },
  { feature: texts.pricing_cards.features.prebuilt_scraper },
  { feature: texts.pricing_cards.features.proxy_management },
  { feature: texts.pricing_cards.features.scalable_plans },
  { feature: texts.pricing_cards.features.geo_targeting },
];

const featuresWebAdvanced = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.results_html_json },
  { feature: texts.pricing_cards.features.scheduling },
  { feature: texts.pricing_cards.features.parsed_results },
  { feature: texts.pricing_cards.features.success_rate_100 },
  { feature: texts.pricing_cards.features.sync_async_requests },
  { feature: texts.pricing_cards.features.bulk_upload },
  { feature: texts.pricing_cards.features.api_playground },
];

const featuresResidential = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.ethical_ips_55m },
  { feature: texts.pricing_cards.features.https_socks5_support },
  { feature: texts.pricing_cards.features.response_time_0_5s },
  { feature: texts.pricing_cards.features.success_rate_99_68 },
  { feature: texts.pricing_cards.features.unlimited_threads_concurrent },
  {
    feature: texts.pricing_cards.features.targeting_country_state_city_asn_zip,
  },
  { feature: texts.pricing_cards.features.rotating_sticky_sessions },
  { feature: texts.pricing_cards.features.ip_quality_market_1 },
  { feature: texts.pricing_cards.features.free_support_24_7 },
];

const featuresMobile = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.mobile_proxies_3g_4g_5g },
  { feature: texts.pricing_cards.features.ethical_ips_10m },
  { feature: texts.pricing_cards.features.locations_160_carriers_700 },
  { feature: texts.pricing_cards.features.response_time_0_8s },
  { feature: texts.pricing_cards.features.success_rate_99_48 },
  { feature: texts.pricing_cards.features.https_socks5 },
  { feature: texts.pricing_cards.features.os_filtering_ios_android },
  { feature: texts.pricing_cards.features.unlimited_concurrent_requests },
  { feature: texts.pricing_cards.features.city_level_targeting },
  { feature: texts.pricing_cards.features.support_24_7 },
];

const featuresSiteUnblocker = (texts: TranslationsTypes) => [
  { feature: texts.pricing_cards.features.success_rate_100 },
  { feature: texts.pricing_cards.features.proxy_like_integration },
  { feature: texts.pricing_cards.features.advanced_browser_fingerprinting },
  { feature: texts.pricing_cards.features.human_like_browsing },
  { feature: texts.pricing_cards.features.results_html_javascript },
  { feature: texts.pricing_cards.features.no_captchas },
  { feature: texts.pricing_cards.features.ip_quality_market_1 },
  { feature: texts.pricing_cards.features.support_24_7 },
];

export const featuresMapToProductType: Record<
  Product,
  (texts: TranslationsTypes) => { feature: string }[]
> = {
  // dc
  datacenter: (texts) => featuresDatacenter(texts),
  datacenter_ip: (texts) => featuresDatacenterIp(texts),
  dedicated_datacenter: (texts) => featuresDedicatedDatacenter(texts),
  // isp
  dedicated_isp: (texts) => featuresDedicatedIsp(texts),
  isp_gb: (texts) => featuresIspGb(texts),
  isp_ip: (texts) => featuresIspIp(texts),
  // scrapers
  social_media: (texts) => featuresSocialMedia(texts),
  ecommerce_core: (texts) => featuresEcommerceCore(texts),
  ecommerce_advanced: (texts) => featuresEcommerceAdvanced(texts),
  serp: (texts) => featuresSERP(texts),
  web: (texts) => featuresWebAdvanced(texts), // remove after 1 week
  web_core: (texts) => featuresWebCore(texts),
  web_advanced: (texts) => featuresWebAdvanced(texts),
  // residential
  residential: (texts) => featuresResidential(texts),
  mobile: (texts) => featuresMobile(texts),
  site_unblocker: (texts) => featuresSiteUnblocker(texts),
  site_unblocker_req: (texts) => featuresSiteUnblocker(texts),
  // none and mixed
  none: () => [],
  mixed: () => [],
};
